import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../components/common/Button';
import PhilosophicalCompassPreview from '../components/common/PhilosophicalCompassPreview';
import AuthModal from '../components/auth/AuthModal';
import { useAuth } from '../services/AuthContext';

const constellationDots = [
  { top: '12%', left: '8%', delay: '0s', duration: '6s' },
  { top: '25%', left: '85%', delay: '1.2s', duration: '7s' },
  { top: '45%', left: '15%', delay: '2.4s', duration: '5.5s' },
  { top: '60%', left: '90%', delay: '0.8s', duration: '6.5s' },
  { top: '18%', left: '55%', delay: '3s', duration: '8s' },
  { top: '75%', left: '40%', delay: '1.8s', duration: '7.5s' },
  { top: '35%', left: '70%', delay: '0.4s', duration: '6s' },
  { top: '80%', left: '20%', delay: '2.8s', duration: '5s' },
];

const HomePage = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const { currentUser } = useAuth();

  const openSignIn = () => {
    setAuthMode('signin');
    setAuthModalOpen(true);
  };

  const openSignUp = () => {
    setAuthMode('signup');
    setAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen overflow-y-auto overflow-x-hidden">
      {/* Hero Section — Dark Cinematic */}
      <div className="relative py-20 md:py-32 overflow-hidden" style={{ background: 'radial-gradient(ellipse at 30% 20%, #162a4a 0%, #0a1628 50%, #060e1a 100%)' }}>
        {/* Constellation dots */}
        {constellationDots.map((dot, i) => (
          <div
            key={i}
            className="hero-constellation-dot"
            style={{ top: dot.top, left: dot.left, animationDelay: dot.delay, animationDuration: dot.duration }}
          />
        ))}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 tracking-tight"
              initial={{ opacity: 0, y: -20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1 }}
            >
              Ancient Wisdom Meets <br />
              <span className="text-shimmer">Modern Intelligence</span>
            </motion.h1>

            <motion.p
              className="max-w-2xl mx-auto text-xl text-white/70 mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Transform timeless Greek philosophy into a personalized journey of growth and critical thinking for today's challenges.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row justify-center gap-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              {currentUser ? (
                <Link to="/journal">
                  <Button size="lg">Continue Your Journey</Button>
                </Link>
              ) : (
                <Link to="/onboarding">
                  <Button size="lg">Try the Demo</Button>
                </Link>
              )}
              <button
                className="px-6 py-3 text-lg font-medium border border-white/30 text-white rounded-md hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-oliveGold/50"
                onClick={() => {
                  if (typeof window.HTMLDialogElement === 'function') {
                    document.getElementById('waitlist-modal').showModal();
                  } else {
                    document.getElementById('waitlist-modal').style.display = 'block';
                  }
                }}
              >
                Join the Waitlist
              </button>
            </motion.div>

            <motion.div
              className="mt-6 flex flex-col sm:flex-row justify-center gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              {!currentUser ? (
                <>
                  <button className="text-white/60 hover:text-oliveGold transition-colors text-sm" onClick={openSignIn}>Sign In</button>
                  <button className="text-white/60 hover:text-oliveGold transition-colors text-sm" onClick={openSignUp}>Create Account</button>
                </>
              ) : (
                <p className="text-sm text-white/60">Welcome back, {currentUser.name}</p>
              )}
            </motion.div>

            {/* Video Guide */}
            <motion.div
              className="mt-12 flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
            >
              <div className="w-full max-w-2xl">
                <div className="relative w-full ring-1 ring-oliveGold/30 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://embed.app.guidde.com/playbooks/uVJD65oRSPev2xRusbfy51"
                    title="Use the AGR demo app"
                    frameBorder="0"
                    referrerPolicy="unsafe-url"
                    allowFullScreen="true"
                    allow="clipboard-write"
                    sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section — Dark */}
      <div className="py-16" style={{ background: '#0f1d32' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-white mb-4">
              The Path to Wisdom
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-white/60">
              Our platform connects you with ancient Greek philosophy, history and mythology through cutting-edge AI technology.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {/* Journal */}
            <div className="border-l-4 border-oliveGold bg-oliveGold/5 rounded-r-lg p-6">
              <div className="w-12 h-12 mb-4 rounded-full bg-oliveGold/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-oliveGold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-serif font-semibold text-white mb-2">
                The Journal
              </h3>
              <p className="text-sm text-oliveGold/80 mb-3 font-medium">AI-Powered Philosophical Guidance</p>
              <p className="text-white/60">
                Your personal space for reflection and growth, enhanced by AI-guided insights and daily challenges.
              </p>
            </div>

            {/* Study */}
            <div className="border-l-4 border-aegeanBlue bg-aegeanBlue/5 rounded-r-lg p-6">
              <div className="w-12 h-12 mb-4 rounded-full bg-aegeanBlue/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-aegeanBlue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-serif font-semibold text-white mb-2">
                The Study
              </h3>
              <p className="text-sm text-aegeanBlue/80 mb-3 font-medium" style={{ color: '#5b8dd4' }}>Personal Growth Journey</p>
              <p className="text-white/60">
                Dive deep into ancient Greek knowledge through interactive learning experiences and personalized pathways.
              </p>
            </div>

            {/* Agora */}
            <div className="border-l-4 border-philosophicalPurple bg-philosophicalPurple/5 rounded-r-lg p-6">
              <div className="w-12 h-12 mb-4 rounded-full bg-philosophicalPurple/20 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-philosophicalPurple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-serif font-semibold text-white mb-2">
                The Agora
              </h3>
              <p className="text-sm mb-3 font-medium" style={{ color: '#9b84b7' }}>Philosophical Community</p>
              <p className="text-white/60">
                Join symposiums and debates and connect with a community of fellow knowledge seekers.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section — Dark */}
      <div className="py-16" style={{ background: '#0a1628' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-white/60">
              Your journey through ancient wisdom is as unique as you are.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { num: '1', title: 'Personalization', desc: 'Answer a few questions about your interests and goals to customize your experience.' },
              { num: '2', title: 'Daily Practice', desc: 'Engage with philosophical challenges and micro-journaling prompts tailored to your needs.' },
              { num: '3', title: 'Deep Learning', desc: 'Explore the knowledge base and engage with AI philosophers to deepen your understanding.' },
              { num: '4', title: 'Community Growth', desc: 'Join discussions and debates with other philosophical minds in the Agora.' },
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-oliveGold text-white flex items-center justify-center font-bold text-xl">
                  {step.num}
                </div>
                <h3 className="text-lg font-medium text-white mb-2">{step.title}</h3>
                <p className="text-white/60">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Philosophical Compass Preview Section */}
      <PhilosophicalCompassPreview />

      {/* CTA Section */}
      <div className="py-16" style={{ background: 'linear-gradient(180deg, #0f1d32 0%, #162a4a 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif font-bold text-white mb-4">
            Ready to Begin Your Philosophical Journey?
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-white/60 mb-8">
            Join thousands of seekers exploring ancient wisdom with modern technology.
          </p>
          {currentUser ? (
            <Link to="/journal">
              <Button size="lg">Continue Your Journey</Button>
            </Link>
          ) : (
            <Link to="/onboarding">
              <Button size="lg">Start Now</Button>
            </Link>
          )}
        </div>
      </div>

      {/* Waitlist Modal */}
      <dialog id="waitlist-modal" className="modal p-0 rounded-lg shadow-elegant max-w-md w-full bg-white">
        <div className="p-6">
          <h3 className="text-2xl font-serif font-bold text-aegeanBlue mb-4">Join Our Waitlist</h3>
          <p className="text-aegeanBlue/80 mb-6">
            Be the first to know when The Oikosystem launches. We'll notify you as soon as it's ready.
          </p>

          <form name="waitlist" method="POST" netlify className="space-y-4">
            <input type="hidden" name="form-name" value="waitlist" />

            <p>
              <label className="block text-sm font-medium text-aegeanBlue/70 mb-1">
                Email
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full mt-1 px-4 py-2 border border-aegeanBlue/20 rounded-md focus:ring-2 focus:ring-oliveGold/50 focus:border-oliveGold outline-none"
                  placeholder="you@example.com"
                />
              </label>
            </p>

            <p>
              <label className="block text-sm font-medium text-aegeanBlue/70 mb-1">
                Name (Optional)
                <input
                  type="text"
                  name="name"
                  className="w-full mt-1 px-4 py-2 border border-aegeanBlue/20 rounded-md focus:ring-2 focus:ring-oliveGold/50 focus:border-oliveGold outline-none"
                  placeholder="Your name"
                />
              </label>
            </p>

            <p className="flex items-start">
              <label className="flex items-start">
                <input
                  name="interest"
                  type="checkbox"
                  className="h-4 w-4 mt-1 text-oliveGold border-aegeanBlue/20 rounded focus:ring-oliveGold/50"
                />
                <span className="ml-3 text-sm text-aegeanBlue/70">
                  I'd like to participate in beta testing
                </span>
              </label>
            </p>

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => document.getElementById('waitlist-modal').close()}
                type="button"
              >
                Cancel
              </Button>
              <Button type="submit">Submit</Button>
            </div>
          </form>
        </div>
      </dialog>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authMode}
      />
    </div>
  );
};

export default HomePage;
